import type { Writable } from "svelte/store";
import type { WebdavConfig } from "./fs";
import { FS } from "./fs";
import { FsDir } from "./fsentry";
import type { HttpProgress } from "./http";
import { mkURL } from "./http";
import { throwIf } from "./util";

// The browser cannot call an arbitrary WebDAV host. File calls go through
// /api/webdav. The user and password are request headers, not query parameters.

class FileSystemWebdav extends FS {
  supportsDirRename = true;

  constructor(config: WebdavConfig) {
    super(config);
  }

  get webdavConfig(): WebdavConfig {
    return this.config as WebdavConfig;
  }

  async readDir(dirPath: string, force: boolean, progress: Writable<any>): Promise<FsDir> {
    throwIf(dirPath == "", "empty dir");
    return this.loadDir(dirPath, force, progress, async () => {
      let entries = await webdavList(this.webdavConfig, dirPath);
      return FsDir.fromWire(dirPath, entries);
    });
  }

  async getPublicURL(path: string): Promise<string> {
    let blob = await this.downloadFile(path, () => {});
    return URL.createObjectURL(blob);
  }

  async downloadFile(path: string, httpProgress: HttpProgress): Promise<Blob> {
    let url = webdavURL(this.webdavConfig, "/api/webdav/download", "path", path);
    let xhr = await webdavSend("GET", url, this.webdavConfig, undefined, httpProgress, "blob");
    return xhr.response as Blob;
  }

  async uploadFile(path: string, file: Blob, httpProgress: HttpProgress): Promise<void> {
    let url = webdavURL(this.webdavConfig, "/api/webdav/upload", "path", path);
    await webdavSend("PUT", url, this.webdavConfig, file, httpProgress);
  }

  async rename(path: string, newPath: string): Promise<void> {
    throwIf(path == newPath, "path must be != newPath");
    let url = webdavURL(this.webdavConfig, "/api/webdav/rename", "path", path, "newPath", newPath);
    await webdavCall(this.webdavConfig, url, "POST");
  }

  async createDirectory(path: string): Promise<void> {
    let url = webdavURL(this.webdavConfig, "/api/webdav/mkdir", "path", path);
    await webdavCall(this.webdavConfig, url, "POST");
  }

  async deleteFile(path: string): Promise<void> {
    let url = webdavURL(this.webdavConfig, "/api/webdav/delete", "path", path);
    await webdavCall(this.webdavConfig, url, "POST");
  }
}

export async function testWebdavConnection(config: WebdavConfig): Promise<{ host: string }> {
  let js = await webdavCall(config, webdavURL(config, "/api/webdav/test"));
  if (!js.host) {
    throw new Error("webdav did not return a host");
  }
  return { host: js.host };
}

async function webdavList(config: WebdavConfig, dir: string): Promise<any[][]> {
  let js = await webdavCall(config, webdavURL(config, "/api/webdav/list", "dir", dir));
  if (!Array.isArray(js)) {
    throw new Error("webdav list failed");
  }
  return js;
}

function webdavURL(config: WebdavConfig, path: string, ...args: string[]): string {
  return mkURL(path, "url", config.url, ...args);
}

function encodeSecret(s: string): string {
  let bytes = new TextEncoder().encode(s);
  let bin = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(bin);
}

function webdavHeaders(config: WebdavConfig): Record<string, string> {
  let headers: Record<string, string> = {};
  if (config.user) {
    headers["X-Webdav-User"] = encodeSecret(config.user);
  }
  if (config.password) {
    headers["X-Webdav-Password"] = encodeSecret(config.password);
  }
  return headers;
}

async function webdavCall(config: WebdavConfig, url: string, method = "GET"): Promise<any> {
  let rsp = await fetch(url, { method, headers: webdavHeaders(config) });
  let text = await rsp.text();
  let js: any = {};
  if (text) {
    try {
      js = JSON.parse(text);
    } catch {
      js = {};
    }
  }
  if (!rsp.ok) {
    throw new Error(js.error || `webdav ${rsp.status}`);
  }
  return js;
}

function webdavSend(
  method: string,
  url: string,
  config: WebdavConfig,
  data: Blob | undefined,
  onProgress: HttpProgress | undefined,
  responseType?: XMLHttpRequestResponseType,
): Promise<XMLHttpRequest> {
  let headers = webdavHeaders(config);
  return new Promise((resolve, reject) => {
    let xhr = new XMLHttpRequest();
    xhr.open(method, url);
    if (responseType) {
      xhr.responseType = responseType;
    }
    for (let [k, v] of Object.entries(headers)) {
      xhr.setRequestHeader(k, v);
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr);
        return;
      }
      webdavErrText(xhr).then(
        (msg) => reject(new Error(msg)),
        () => reject(new Error(`webdav ${xhr.status}`)),
      );
    };
    xhr.onerror = () => {
      reject(new Error("webdav request failed"));
    };
    if (onProgress) {
      let started = Date.now();
      let report = (ev: ProgressEvent) => {
        let total = ev.lengthComputable ? ev.total : 0;
        onProgress(ev.loaded, total, Date.now() - started);
      };
      if (method == "PUT") {
        xhr.upload.onprogress = report;
      } else {
        xhr.onprogress = report;
      }
    }
    if (data) {
      xhr.send(data);
    } else {
      xhr.send();
    }
  });
}

async function webdavErrText(xhr: XMLHttpRequest): Promise<string> {
  let text = "";
  if (xhr.responseType == "blob" && xhr.response instanceof Blob) {
    text = await xhr.response.text();
  } else if (xhr.responseType == "" || xhr.responseType == "text") {
    text = xhr.responseText || "";
  }
  if (text) {
    try {
      let js = JSON.parse(text);
      if (js.error) {
        return String(js.error);
      }
    } catch {
      // keep the status
    }
  }
  return `webdav ${xhr.status}`;
}

export function makeFileSystemWebdav(config: WebdavConfig): FileSystemWebdav {
  return new FileSystemWebdav(config);
}
